"use client";
import { useCallback, useEffect, useState } from "react";

import { addressesApi } from "@/api/services";
import { useAuth } from "@/store/auth";
import type {
  AddressCreate,
  AddressUpdate,
  AppSchemasCustomerAddress as CustomerAddress,
} from "@/api/online-ordering-service/model";

type UseAddressesResult = {
  addresses: CustomerAddress[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  createAddress: (input: AddressCreate) => Promise<CustomerAddress>;
  updateAddress: (
    id: string,
    input: AddressUpdate,
  ) => Promise<CustomerAddress>;
  deleteAddress: (id: string) => Promise<void>;
  getAddress: (id: string) => Promise<CustomerAddress>;
};

/**
 * Manages the current customer's saved addresses.
 *
 * - When the user is not logged in, returns an empty list (no error, no fetch).
 * - On login state change, refetches automatically.
 * - All CRUD operations call back into `addressesApi.list()` to keep state in
 *   sync with the server (instead of optimistic updates), which is good
 *   enough for this small surface.
 */
export function useAddresses(): UseAddressesResult {
  const { isLoggedIn } = useAuth();
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!isLoggedIn) {
      setAddresses([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await addressesApi.list();
      setAddresses(list);
    } catch (err) {
      console.warn("[useAddresses] list failed", err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createAddress = useCallback(
    async (input: AddressCreate) => {
      const created = await addressesApi.create(input);
      await refresh();
      return created;
    },
    [refresh],
  );

  const updateAddress = useCallback(
    async (id: string, input: AddressUpdate) => {
      const updated = await addressesApi.update(id, input);
      await refresh();
      return updated;
    },
    [refresh],
  );

  const deleteAddress = useCallback(
    async (id: string) => {
      await addressesApi.delete(id);
      await refresh();
    },
    [refresh],
  );

  const getAddress = useCallback(async (id: string) => {
    return await addressesApi.get(id);
  }, []);

  return {
    addresses,
    loading,
    error,
    refresh,
    createAddress,
    updateAddress,
    deleteAddress,
    getAddress,
  };
}
